import { useEffect, useState } from 'react';
import { useApi } from '../../services/api';

export default function DepartmentManagement() {
    const api = useApi();
    const [departments, setDepartments] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showMembersModal, setShowMembersModal] = useState(false);
    const [newDept, setNewDept] = useState({ name: '', description: '' });
    const [editingDept, setEditingDept] = useState<any>(null);
    const [viewingDept, setViewingDept] = useState<any>(null);
    const [deptMembers, setDeptMembers] = useState<any[]>([]);

    useEffect(() => {
        loadDepartments();
    }, []);

    async function loadDepartments() {
        try {
            setLoading(true);
            const result = await api.getDepartments();
            setDepartments(result.departments || []);
        } catch (error) {
            console.error('Failed to load departments:', error);
        } finally {
            setLoading(false);
        }
    }

    async function handleCreate() {
        if (!newDept.name.trim()) {
            alert('Department name is required');
            return;
        }

        try {
            await api.createDepartment(newDept);
            setShowCreateModal(false);
            setNewDept({ name: '', description: '' });
            await loadDepartments();
        } catch (error) {
            console.error('Failed to create department:', error);
            alert('Failed to create department');
        }
    }

    async function handleViewMembers(dept: any) {
        try {
            setViewingDept(dept);
            const result = await api.getDepartmentMembers(dept.id);
            setDeptMembers(result.employees || []);
            setShowMembersModal(true);
        } catch (error) {
            console.error('Failed to load department members:', error);
            alert('Failed to load members');
        }
    }

    function handleEdit(dept: any) {
        setEditingDept(dept);
        setShowEditModal(true);
    }

    async function handleUpdate() {
        if (!editingDept?.name.trim()) {
            alert('Department name is required');
            return;
        }

        try {
            await api.updateDepartment(editingDept.id, {
                name: editingDept.name,
                description: editingDept.description,
            });
            setShowEditModal(false);
            setEditingDept(null);
            await loadDepartments();
        } catch (error) {
            console.error('Failed to update department:', error);
            alert('Failed to update department');
        }
    }

    async function handleDelete() {
        if (!confirm('Are you sure you want to delete this department?')) {
            return;
        }

        try {
            alert('Delete functionality coming soon');
        } catch (error) {
            console.error('Failed to delete department:', error);
            alert('Failed to delete department');
        }
    }

    if (loading) {
        return <div className="text-center py-12 text-text-secondary">Loading departments...</div>;
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-text-primary">Department Management</h1>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-primary text-white px-4 py-2 rounded hover:opacity-90"
                >
                    + Create Department
                </button>
            </div>

            {/* Departments Grid */}
            {departments.length === 0 ? (
                <div className="bg-surface rounded-lg shadow p-12 text-center border border-border">
                    <p className="text-text-secondary text-lg mb-4">No departments found</p>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="text-primary hover:underline"
                    >
                        Create your first department
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {departments.map((dept) => (
                        <div
                            key={dept.id}
                            className="bg-surface rounded-lg shadow p-6 hover:shadow-lg transition border border-border"
                        >
                            <h3 className="text-xl font-bold mb-2 text-text-primary">{dept.name}</h3>
                            <p className="text-text-muted text-sm mb-4">
                                {dept.description || 'No description'}
                            </p>
                            <div className="text-sm text-text-secondary mb-4">
                                👥 {dept.employeeCount || 0} members
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleViewMembers(dept)}
                                    className="flex-1 bg-blue-500/20 text-blue-400 px-3 py-2 rounded hover:bg-blue-500/30 text-sm border border-blue-500/30"
                                >
                                    View Members
                                </button>
                                <button
                                    onClick={() => handleEdit(dept)}
                                    className="flex-1 bg-background text-text-secondary px-3 py-2 rounded hover:bg-border text-sm border border-border"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleDelete()}
                                    className="flex-1 bg-red-500/20 text-red-400 px-3 py-2 rounded hover:bg-red-500/30 text-sm border border-red-500/30"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-surface rounded-lg p-6 w-full max-w-md border border-border">
                        <h2 className="text-2xl font-bold mb-4 text-text-primary">Create Department</h2>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-text-secondary mb-1">
                                Department Name *
                            </label>
                            <input
                                type="text"
                                value={newDept.name}
                                onChange={(e) => setNewDept({ ...newDept, name: e.target.value })}
                                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                                placeholder="e.g., Engineering"
                            />
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-text-secondary mb-1">
                                Description
                            </label>
                            <textarea
                                value={newDept.description}
                                onChange={(e) => setNewDept({ ...newDept, description: e.target.value })}
                                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                                rows={3}
                                placeholder="Optional description"
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="flex-1 bg-background text-text-secondary px-4 py-2 rounded hover:bg-border border border-border"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreate}
                                className="flex-1 bg-primary text-white px-4 py-2 rounded hover:opacity-90"
                            >
                                Create
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && editingDept && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-surface rounded-lg p-6 w-full max-w-md border border-border">
                        <h2 className="text-2xl font-bold mb-4 text-text-primary">Edit Department</h2>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-text-secondary mb-1">
                                Department Name *
                            </label>
                            <input
                                type="text"
                                value={editingDept.name}
                                onChange={(e) => setEditingDept({ ...editingDept, name: e.target.value })}
                                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                            />
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-text-secondary mb-1">
                                Description
                            </label>
                            <textarea
                                value={editingDept.description}
                                onChange={(e) => setEditingDept({ ...editingDept, description: e.target.value })}
                                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                                rows={3}
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowEditModal(false);
                                    setEditingDept(null);
                                }}
                                className="flex-1 bg-background text-text-secondary px-4 py-2 rounded hover:bg-border border border-border"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdate}
                                className="flex-1 bg-primary text-white px-4 py-2 rounded hover:opacity-90"
                            >
                                Update
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Members Modal */}
            {showMembersModal && viewingDept && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-surface rounded-lg p-6 w-full max-w-2xl border border-border">
                        <h2 className="text-2xl font-bold mb-4 text-text-primary">{viewingDept.name} - Members</h2>

                        {deptMembers.length === 0 ? (
                            <p className="text-text-secondary py-8 text-center">No members in this department</p>
                        ) : (
                            <div className="max-h-96 overflow-y-auto">
                                <table className="w-full">
                                    <thead className="bg-background">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-sm font-medium text-text-secondary">Name</th>
                                            <th className="px-4 py-2 text-left text-sm font-medium text-text-secondary">Email</th>
                                            <th className="px-4 py-2 text-left text-sm font-medium text-text-secondary">Position</th>
                                            <th className="px-4 py-2 text-left text-sm font-medium text-text-secondary">Role</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {deptMembers.map((member) => (
                                            <tr key={member.id} className="hover:bg-background">
                                                <td className="px-4 py-3 text-sm text-text-primary">{member.name}</td>
                                                <td className="px-4 py-3 text-sm text-text-secondary">{member.email}</td>
                                                <td className="px-4 py-3 text-sm text-text-secondary">{member.position || '-'}</td>
                                                <td className="px-4 py-3 text-sm">
                                                    <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs border border-blue-500/30">
                                                        {member.role}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="mt-6">
                            <button
                                onClick={() => {
                                    setShowMembersModal(false);
                                    setViewingDept(null);
                                    setDeptMembers([]);
                                }}
                                className="w-full bg-background text-text-secondary px-4 py-2 rounded hover:bg-border border border-border"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

